import React, { useState, useEffect, useMemo } from "react";
import "./styles/Dashboard.css";
import { deleteEvent, getAllEvents, getEventsCount, updateEventisLive } from "../api/EventsAPI.js";
import { getAllRequests, getRequestsCount, deleteRequest } from "../api/ContactAPI.js";
import { useAuth } from "../contexts/AuthContext.js";
import { getAllUsers, getUsersCount } from "../api/UserAPI.js";
import { getAllReports, getReportsCount } from "../api/ReportsAPI.js";
import { getAllMovements, getMovementsCount } from "../api/MovementsAPI.js";

import ContactMailIcon from '@mui/icons-material/ContactMail';
import Groups3Icon from '@mui/icons-material/Groups3';
import StadiumIcon from '@mui/icons-material/Stadium';
import ErrorIcon from '@mui/icons-material/Error';
import ForestIcon from '@mui/icons-material/Forest';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';

import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

import Requests from "./pages/Requests.jsx";
import Users from "./pages/Users.jsx";
import Communities from "./pages/Communities.jsx";
import Events from "./pages/Events.jsx";
import Errors from "./pages/Errors.jsx";
import Reports from "./pages/Reports.jsx";
import Movements from "./pages/Movements.jsx";

import ConfirmDialog from "./components/ConfirmDialog.jsx";
import { getAllCommunities, getCommunitiesCount } from "../api/CommunitiesAPI.js";

// ── Chart helpers ─────────────────────────────────────────────────────────────

const CHART_MODES = { PLATFORM: 'platform', DEVICE: 'device' };

const CHART_LABELS = {
    [CHART_MODES.PLATFORM]: 'Mobile vs Web',
    [CHART_MODES.DEVICE]: 'Android vs iOS',
};

const TOOLTIP_STYLE = {
    borderRadius: 8, border: 'none',
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: 13,
};

const AXIS_PROPS = {
    tick: { fontSize: 12, fill: '#6b7280' },
    axisLine: false,
    tickLine: false,
};

function resolveDate(createdAt) {
    if (!createdAt) return null;
    const d = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
    return isNaN(d) ? null : d;
}

function monthKey(date) {
    return date.toLocaleString('default', { month: 'short', year: '2-digit' });
}

function sortMonthEntries(entries) {
    const msMap = {};
    entries.forEach(([k]) => {
        if (!msMap[k]) {
            const [mon, yr] = k.split(' ');
            msMap[k] = new Date(`${mon} 20${yr}`).getTime();
        }
    });
    return [...entries].sort((a, b) => msMap[a[0]] - msMap[b[0]]);
}

function filterByDateRange(users, startDate, endDate) {
    if (!startDate && !endDate) return users;
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate + 'T23:59:59') : null;
    return users.filter((u) => {
        const d = resolveDate(u.createdAt);
        if (!d) return false;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
    });
}

function buildGrowthData(users) {
    const counts = {};
    users.forEach((u) => {
        const d = resolveDate(u.createdAt);
        if (!d) return;
        const k = monthKey(d);
        counts[k] = (counts[k] || 0) + 1;
    });
    let cum = 0;
    return sortMonthEntries(Object.entries(counts)).map(([month, n]) => {
        cum += n;
        return { month, users: cum };
    });
}

function buildCategoryData(users, keyTrue, keyFalse, predicate, preFilter) {
    const byMonth = {};
    (preFilter ? users.filter(preFilter) : users).forEach((u) => {
        const d = resolveDate(u.createdAt);
        if (!d) return;
        const k = monthKey(d);
        if (!byMonth[k]) byMonth[k] = { [keyTrue]: 0, [keyFalse]: 0 };
        predicate(u) ? byMonth[k][keyTrue]++ : byMonth[k][keyFalse]++;
    });
    return sortMonthEntries(Object.entries(byMonth)).map(([month, v]) => ({ month, ...v }));
}

const buildPlatformData = (users) =>
    buildCategoryData(users, 'mobile', 'web', (u) => u.onboardingCompleted === true);

const buildDeviceData = (users) =>
    buildCategoryData(
        users, 'ios', 'android',
        (u) => u.appMetaData?.platform === 'ios',
        (u) => u.onboardingCompleted === true
    );

// ── Shared sub-components ─────────────────────────────────────────────────────

function DateRangeFilter({ startDate, endDate, setStartDate, setEndDate }) {
    return (
        <div className="chart-date-range">
            <label className="chart-date-label">From</label>
            <input
                type="date" className="chart-date-input"
                value={startDate} onChange={(e) => setStartDate(e.target.value)}
            />
            <label className="chart-date-label">To</label>
            <input
                type="date" className="chart-date-input"
                value={endDate} onChange={(e) => setEndDate(e.target.value)}
            />
            {(startDate || endDate) && (
                <button
                    className="chart-date-clear"
                    onClick={() => { setStartDate(''); setEndDate(''); }}
                    title="Clear date range"
                >✕</button>
            )}
        </div>
    );
}

function ChartEmpty() {
    return <div className="chart-empty">No data available for the selected range</div>;
}

// ── GrowthChart (left) ────────────────────────────────────────────────────────

function GrowthChart({ users }) {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const filtered = useMemo(() => filterByDateRange(users, startDate, endDate), [users, startDate, endDate]);
    const chartData = useMemo(() => buildGrowthData(filtered), [filtered]);
    const chartColor = '#7c3aed';

    return (
        <div className="user-growth-chart">
            <div className="chart-toolbar">
                <h3 className="chart-heading">User Growth</h3>
                {/* <DateRangeFilter
                    startDate={startDate} endDate={endDate}
                    setStartDate={setStartDate} setEndDate={setEndDate}
                /> */}
            </div>
            {chartData.length === 0 ? <ChartEmpty /> : (
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="gc-purple" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={chartColor} stopOpacity={0.2} />
                                <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" {...AXIS_PROPS} />
                        <YAxis {...AXIS_PROPS} width={36} />
                        <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ fontWeight: 600 }} />
                        <Area type="monotone" dataKey="users" name="Total Users"
                            stroke={chartColor} strokeWidth={2.5} fill="url(#gc-purple)"
                            dot={{ r: 3, fill: chartColor, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </div>
    );
}

// ── PlatformChart (right) ─────────────────────────────────────────────────────

const PLATFORM_LEGEND = [
    { color: '#3acced', label: 'Mobile (App)', key: 'mobile' },
    { color: '#dc7e26', label: 'Web', key: 'web' },
];
const DEVICE_LEGEND = [
    { color: '#059669', label: 'Android', key: 'android' },
    { color: '#2563eb', label: 'iOS', key: 'ios' },
];

function PlatformChart({ users }) {
    const [mode, setMode] = useState(CHART_MODES.PLATFORM);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const filtered = useMemo(() => filterByDateRange(users, startDate, endDate), [users, startDate, endDate]);
    const chartData = useMemo(() => (
        mode === CHART_MODES.PLATFORM ? buildPlatformData(filtered) : buildDeviceData(filtered)
    ), [filtered, mode]);

    const legendItems = mode === CHART_MODES.PLATFORM ? PLATFORM_LEGEND : DEVICE_LEGEND;

    const totals = useMemo(() => {
        const sums = {};
        legendItems.forEach(({ key }) => {
            sums[key] = chartData.reduce((acc, row) => acc + (row[key] || 0), 0);
        });
        return sums;
    }, [chartData, legendItems]);

    return (
        <div className="user-growth-chart">
            <div className="chart-toolbar">
                <h3 className="chart-heading">{CHART_LABELS[mode]}</h3>
                <div className="chart-controls">
                    <div className="chart-mode-toggle">
                        {Object.entries(CHART_LABELS).map(([key, label]) => (
                            <button
                                key={key}
                                className={`chart-toggle-btn${mode === key ? ' active' : ''}`}
                                onClick={() => setMode(key)}
                            >{label}</button>
                        ))}
                    </div>
                    {/* <DateRangeFilter
                        startDate={startDate} endDate={endDate}
                        setStartDate={setStartDate} setEndDate={setEndDate}
                    /> */}
                </div>
            </div>
            {chartData.length === 0 ? <ChartEmpty /> : (
                <>
                    <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                            <defs>
                                {mode === CHART_MODES.PLATFORM ? (<>
                                    <linearGradient id="pd-a" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={PLATFORM_LEGEND[0].color} stopOpacity={0.2} />
                                        <stop offset="95%" stopColor={PLATFORM_LEGEND[0].color} stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="pd-b" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={PLATFORM_LEGEND[1].color} stopOpacity={0.2} />
                                        <stop offset="95%" stopColor={PLATFORM_LEGEND[1].color} stopOpacity={0} />
                                    </linearGradient>
                                </>) : (<>
                                    <linearGradient id="pd-a" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={DEVICE_LEGEND[0].color} stopOpacity={0.2} />
                                        <stop offset="95%" stopColor={DEVICE_LEGEND[0].color} stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="pd-b" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={DEVICE_LEGEND[1].color} stopOpacity={0.2} />
                                        <stop offset="95%" stopColor={DEVICE_LEGEND[1].color} stopOpacity={0} />
                                    </linearGradient>
                                </>)}
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" {...AXIS_PROPS} />
                            <YAxis {...AXIS_PROPS} width={36} />
                            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ fontWeight: 600 }} />
                            {mode === CHART_MODES.PLATFORM && (<>
                                <Area type="monotone" dataKey="mobile" name="Mobile (App)"
                                    stroke={PLATFORM_LEGEND[0].color} strokeWidth={2.5} fill="url(#pd-a)"
                                    dot={{ r: 3, fill: PLATFORM_LEGEND[0].color, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                                <Area type="monotone" dataKey="web" name="Web"
                                    stroke={PLATFORM_LEGEND[1].color} strokeWidth={2.5} fill="url(#pd-b)"
                                    dot={{ r: 3, fill: PLATFORM_LEGEND[1].color, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                            </>)}
                            {mode === CHART_MODES.DEVICE && (<>
                                <Area type="monotone" dataKey="ios" name="iOS"
                                    stroke={DEVICE_LEGEND[1].color} strokeWidth={2.5} fill="url(#pd-b)"
                                    dot={{ r: 3, fill: DEVICE_LEGEND[1].color, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                                <Area type="monotone" dataKey="android" name="Android"
                                    stroke={DEVICE_LEGEND[0].color} strokeWidth={2.5} fill="url(#pd-a)"
                                    dot={{ r: 3, fill: DEVICE_LEGEND[0].color, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                            </>)}
                        </AreaChart>
                    </ResponsiveContainer>
                    <div className="chart-legend">
                        {legendItems.map(({ color, label, key }) => (
                            <span key={label} className="chart-legend-item">
                                <span className="chart-legend-dot" style={{ background: color }} />
                                {label}
                                <span className="chart-legend-count">{totals[key].toLocaleString()}</span>
                            </span>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default function Dashboard() {
    const { user, role } = useAuth();

    const [showConfirmDialog, setshowConfirmDialog] = useState(false);
    const [confirmDialogProps, setconfirmDialogProps] = useState({
        message: "Are you sure you want to delete this request?",
        actionKey: "delete",
        recordId: null
    });

    const [activeTab, setActiveTab] = useState('users');
    const [dataCounts, setdataCounts] = useState({
        requests: 0, users: 0, communities: 0, events: 0, reports: 0, movements: 0
    });

    const [contactRequests, setcontactRequests] = useState([]);
    const [users, setusers] = useState([]);
    const [communities, setcommunities] = useState([]);
    const [events, setevents] = useState([]);
    const [reports, setreports] = useState([]);
    const [movements, setmovements] = useState([]);

    const avatarUrl = user.photoURL || "undefined";

    const userTrend = useMemo(() => {
        if (users.length === 0) return null;
        const now = new Date();
        const thisMonth = now.getMonth();
        const thisYear = now.getFullYear();
        const prevMonth = thisMonth === 0 ? 11 : thisMonth - 1;
        const prevYear = thisMonth === 0 ? thisYear - 1 : thisYear;
        let current = 0, previous = 0;
        users.forEach((u) => {
            const d = resolveDate(u.createdAt);
            if (!d) return;
            if (d.getFullYear() === thisYear && d.getMonth() === thisMonth) current++;
            if (d.getFullYear() === prevYear && d.getMonth() === prevMonth) previous++;
        });

        console.log("C: " + current)
        console.log("P: " + previous)

        if (previous === 0) return null;
        const pct = ((current - previous) / previous) * 100;
        return { pct: Math.abs(pct).toFixed(1), up: pct >= 0 };
    }, [users]);

    useEffect(() => {
        async function getCounts() {
            const [reqRes, commRes, userRes, eventsRes, reportsRes, movementsRes] = await Promise.all([
                getRequestsCount(),
                getCommunitiesCount(),
                getUsersCount(),
                getEventsCount(),
                getReportsCount(),
                getMovementsCount(),
            ]);
            setdataCounts({
                requests: reqRes.count ?? 0,
                communities: commRes.count ?? 0,
                users: userRes.count ?? 0,
                events: eventsRes.count ?? 0,
                reports: reportsRes.count ?? 0,
                movements: movementsRes.count ?? 0,
            });
        }
        getCounts();
    }, []);

    useEffect(() => {
        switch (activeTab) {
            case 'requests': initRequests(); break;
            case 'users': initUsers(); break;
            case 'communities': initCommunities(); break;
            case 'events': initEvents(); break;
            case 'errors': initEvents(); break;
            case 'reports': initReports(); break;
            case 'movements': initMovements(); break;
            default: break;
        }
    }, [activeTab]);

    function toggleConfirmDialog(show, message, actionKey, recordIdIn) {
        if (show) {
            const recordId = recordIdIn || null;
            setconfirmDialogProps({ message, actionKey, recordId });
            setshowConfirmDialog(show);
        } else {
            setshowConfirmDialog(!showConfirmDialog);
        }
    }

    async function initRequests() {
        const res = await getAllRequests();
        if (res.success) setcontactRequests(res.requests);
    }

    async function initUsers() {
        const res = await getAllUsers();
        if (res.success) setusers(res.users);
    }

    async function initCommunities() {
        const res = await getAllCommunities();
        if (res.success) setcommunities(res.communities);
    }

    async function initEvents() {
        const res = await getAllEvents();
        if (res.success) setevents(res.events);
    }

    async function initReports() {
        const res = await getAllReports();
        if (res.success) setreports(res.reports);
    }

    async function initMovements() {
        const res = await getAllMovements();
        if (res.success) setmovements(res.movements);
    }

    const tabsActive = [
        {
            id: 'users', name: 'Users', value: dataCounts.users, colorKey: 'purple',
            icon: <Groups3Icon sx={{ fontSize: 26, color: '#fff' }} />,
            trend: userTrend,
            onClick: () => setActiveTab('users')
        },
        {
            id: 'requests', name: 'Contact Requests', value: dataCounts.requests, colorKey: 'blue',
            icon: <ContactMailIcon sx={{ fontSize: 26, color: '#fff' }} />,
            onClick: () => setActiveTab('requests')
        },
        {
            id: 'reports', name: 'Reports', value: dataCounts.reports, colorKey: 'coral',
            icon: <AssignmentIcon sx={{ fontSize: 26, color: '#fff' }} />,
            onClick: () => setActiveTab('reports')
        },
        {
            id: 'communities', name: 'Communities', value: dataCounts.communities, colorKey: 'green',
            icon: <ForestIcon sx={{ fontSize: 26, color: '#fff' }} />,
            onClick: () => setActiveTab('communities')
        },
        {
            id: 'events', name: 'Events', value: dataCounts.events, colorKey: 'orange',
            icon: <StadiumIcon sx={{ fontSize: 26, color: '#fff' }} />,
            onClick: () => setActiveTab('events')
        },
        {
            id: 'movements', name: 'Movements', value: movements.length || dataCounts.movements, colorKey: 'teal',
            icon: <DirectionsRunIcon sx={{ fontSize: 26, color: '#fff' }} />,
            onClick: () => setActiveTab('movements')
        },
        {
            id: 'errors', name: 'Testing', value: null, colorKey: 'slate',
            icon: <ErrorIcon sx={{ fontSize: 26, color: '#fff' }} />,
            onClick: () => setActiveTab('errors')
        },
    ];

    const renderContent = () => {
        switch (activeTab) {
            case 'requests':
                return (
                    <Requests
                        contactRequests={contactRequests}
                        setcontactRequests={setcontactRequests}
                        toggleConfirmDialog={toggleConfirmDialog}
                    />
                );
            case 'users':
                return <Users users={users} />;
            case 'communities':
                return <Communities communities={communities} />;
            case 'events':
                return (
                    <Events
                        user={user}
                        events={events}
                        toggleConfirmDialog={toggleConfirmDialog}
                    />
                );
            case 'errors':
                return <Errors events={events} />;
            case 'reports':
                return <Reports reports={reports} />;
            case 'movements':
                return <Movements movements={movements} />;
            default:
                return null;
        }
    };

    const handleConfirm = async () => {
        let apiResponse = null;
        const recordId = confirmDialogProps.recordId;

        switch (confirmDialogProps.actionKey) {
            case 'deactivateEvent':
                apiResponse = await updateEventisLive(recordId, false);
                if (apiResponse.success) {
                    setevents(events.map((event) =>
                        event.id === recordId ? { ...event, isLive: false } : event
                    ));
                }
                break;
            case 'activateEvent':
                apiResponse = await updateEventisLive(recordId, true);
                if (apiResponse.success) {
                    setevents(events.map((event) =>
                        event.id === recordId ? { ...event, isLive: true } : event
                    ));
                }
                break;
            case 'deleteRequest':
                apiResponse = await deleteRequest(recordId);
                if (apiResponse.success) {
                    setcontactRequests(contactRequests.filter((r) => r.id !== recordId));
                }
                break;
            case 'deleteEvent':
                apiResponse = await deleteEvent(recordId);
                if (apiResponse.success) {
                    setevents(events.filter((e) => e.id !== recordId));
                }
                break;
            default:
                break;
        }

        setconfirmDialogProps({ message: "", actionKey: null, recordId: null });
        setshowConfirmDialog(false);
    };

    const handleCancel = () => {
        setshowConfirmDialog(false);
    };

    return (
        <div className="dashboard">
            {showConfirmDialog && (
                <ConfirmDialog
                    props={confirmDialogProps}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                />
            )}

            <div className="dashboard-header">
                {avatarUrl !== 'undefined' ? (
                    <img src={avatarUrl} alt={user.displayName} className="avatar-image" />
                ) : (
                    <div className="avatar-fallback">{user.displayName?.charAt(0)}</div>
                )}
                <span className="header-name">{user.displayName}</span>
                <span className="header-role-badge">{role}</span>
            </div>

            <div className="dashboard-stats-row">
                {tabsActive.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={tab.onClick}
                        className={`stat-card stat-card--${tab.colorKey}${activeTab === tab.id ? ' active' : ''}`}
                    >
                        <div className="stat-card__icon-wrap">
                            {tab.icon}
                        </div>
                        <div className="stat-card__body">
                            <span className="stat-card__value">
                                {tab.value !== null ? tab.value : '—'}
                            </span>
                            <span className="stat-card__label">{tab.name}</span>
                        </div>
                        {tab.trend && (
                            <span className={`stat-card__trend stat-card__trend--${tab.trend.up ? 'up' : 'down'}`}>
                                {tab.trend.up ? '↑' : '↓'} {tab.trend.pct}%
                            </span>
                        )}
                    </button>
                ))}
            </div>

            <div className="dashboard-content">
                {activeTab === 'users' && (
                    <div className="charts-row">
                        <GrowthChart users={users} />
                        <PlatformChart users={users} />
                    </div>
                )}
                {renderContent()}
            </div>
        </div>
    );
}
